import EmblaCarousel, { EmblaCarouselType } from "embla-carousel";

/**
 * Mount an Embla carousel on an .embla container with .embla__viewport and
 * .embla__slide children. Wires up a `1/N` counter on a sibling element with
 * data-embla-counter. Returns the Embla instance so callers can destroy it.
 */
function mountListingCarousel(root: HTMLElement): EmblaCarouselType | null {
  const viewport = root.querySelector<HTMLElement>(".embla__viewport");
  if (!viewport) return null;
  const embla = EmblaCarousel(viewport, { loop: false, align: "start" });
  const counter = root.querySelector<HTMLElement>("[data-embla-counter]");
  const totalSlides = root.querySelectorAll(".embla__slide").length;

  // Prev/next arrow buttons are optional (Airbnb mobile relies on swipe only).
  // When present they must drive Embla — without this binding they render but
  // do nothing.
  const prevBtn = root.querySelector<HTMLButtonElement>("[data-embla-prev]");
  const nextBtn = root.querySelector<HTMLButtonElement>("[data-embla-next]");
  prevBtn?.addEventListener("click", () => embla.scrollPrev());
  nextBtn?.addEventListener("click", () => embla.scrollNext());

  const updateControls = () => {
    if (counter) {
      const idx = embla.selectedScrollSnap() + 1;
      counter.textContent = `${idx}/${totalSlides}`;
    }
    // Reflect reachable edges (loop is off) so the arrows look inert only when
    // they truly are.
    if (prevBtn) prevBtn.disabled = !embla.canScrollPrev();
    if (nextBtn) nextBtn.disabled = !embla.canScrollNext();
  };
  embla.on("select", updateControls);
  embla.on("reInit", updateControls);
  updateControls();
  (root as any)._embla = embla;
  return embla;
}

function destroyListingCarousel(root: HTMLElement) {
  const embla = (root as any)._embla as EmblaCarouselType | undefined;
  if (embla) {
    embla.destroy();
    delete (root as any)._embla;
  }
}

// Auto-mount on page load and on HTMX swaps.
function mountAll() {
  document.querySelectorAll<HTMLElement>(".listing-preview-embla").forEach((el) => {
    destroyListingCarousel(el);
    mountListingCarousel(el);
  });
}
// Defer both registrations: document.body is null when this module is
// imported from the bundle (which loads before </body>). DOMContentLoaded
// guarantees body exists by the time mountAll and the htmx listener bind.
document.addEventListener("DOMContentLoaded", () => {
  mountAll();
  document.body.addEventListener("htmx:afterSwap", mountAll);
});

(window as any).mountListingCarousels = mountAll;

// Alpine component for the manager slot picker. Registered from studio-bundle.ts.
// urlMap is an asset-id → presigned-URL map built at server-render time so the
// picker can render <img> for stored slot assignments without an extra API call.
export const registerListingPreview = (Alpine: any) => {
  Alpine.data(
    "listingPreviewPicker",
    (
      deliverableID: string,
      platform: string,
      slotCount: number,
      initialAssetIDs: string[],
      initialTitle: string,
      urlMap: Record<string, string>,
    ) => ({
      deliverableID,
      platform,
      slotCount,
      urlMap,
      slots: [
        ...initialAssetIDs,
        ...Array(Math.max(0, slotCount - initialAssetIDs.length)).fill(null),
      ] as (string | null)[],
      title: initialTitle,
      saving: false,
      // Index of the slot currently "armed" for a tap interaction, or null.
      // Touch has no HTML5 drag-and-drop, so mobile uses tap-to-place: tapping a
      // pool photo commits it (to the armed slot, else the first empty one);
      // tapping a slot arms it, and tapping a second slot swaps the two. Desktop
      // drag-and-drop still works — these handlers coexist with it.
      selectedSlot: null as number | null,

      // Tap a pool photo: place into the armed slot (replace) or the first empty
      // slot. With no armed slot and no empty slot, do nothing — the manager
      // must tap a slot first to choose what to replace.
      tapPoolAsset(assetID: string) {
        if (this.selectedSlot !== null) {
          const target = this.selectedSlot;
          this.selectedSlot = null;
          this.assignSlot(target, assetID);
          return;
        }
        const empty = this.slots.indexOf(null);
        if (empty !== -1) this.assignSlot(empty, assetID);
      },

      // Tap a slot: toggle arm on the same slot; arm when none is armed; swap
      // when a different (filled) slot is already armed.
      tapSlot(idx: number) {
        if (this.selectedSlot === idx) {
          this.selectedSlot = null;
          return;
        }
        if (this.selectedSlot === null) {
          this.selectedSlot = idx;
          return;
        }
        const from = this.selectedSlot;
        const moving = this.slots[from];
        // Armed slot is empty — nothing to move, so just re-arm the new slot.
        if (moving === null) {
          this.selectedSlot = idx;
          return;
        }
        this.selectedSlot = null;
        // assignSlot relocates the displaced asset back to `from`, so this
        // swaps the two slots' contents.
        this.assignSlot(idx, moving);
      },

      async save() {
        this.saving = true;
        try {
          const resp = await fetch(
            `/api/deliverables/${this.deliverableID}/listing-preview/${this.platform}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": document
                  .querySelector("meta[name=csrf-token]")!
                  .getAttribute("content")!,
              },
              body: JSON.stringify({
                title: this.title.trim() === "" ? null : this.title,
                asset_ids: this.slots.filter((id) => id !== null),
              }),
            },
          );
          if (!resp.ok) {
            console.error("[listingPreviewPicker] save failed:", resp.status);
            return;
          }
          // Refresh the rendered preview panels with an innerHTML swap of
          // #listing-preview-panels so the mock reflects the new title /
          // asset order without a full page reload. Only the panels'
          // children are replaced; the outer Alpine scope (picker state,
          // platform/viewport/view toggles) lives above the target and is
          // untouched.
          const htmx = (window as any).htmx;
          if (htmx) {
            htmx.ajax("GET", `/api/deliverables/${this.deliverableID}/listing-preview-panels`, {
              target: "#listing-preview-panels",
              swap: "innerHTML",
            });
          }
        } finally {
          this.saving = false;
        }
      },

      // Called by the picker UI when the manager drops an asset onto a slot.
      assignSlot(slotIndex: number, assetID: string) {
        // If asset already used in another slot, swap them.
        const existingIdx = this.slots.indexOf(assetID);
        if (existingIdx !== -1 && existingIdx !== slotIndex) {
          this.slots[existingIdx] = this.slots[slotIndex];
        }
        this.slots[slotIndex] = assetID;
        this.save();
      },

      clearSlot(slotIndex: number) {
        if (this.selectedSlot === slotIndex) this.selectedSlot = null;
        this.slots[slotIndex] = null;
        this.save();
      },

      onTitleBlur() {
        this.save();
      },
    }),
  );
};
